import { getNativeChainIdForCurrency } from '../config/native'
import { resolveMaxSwapPriceImpactPercent } from '../config/marketPolicy'
import type { ChainId } from '../sdk'
import {
  Currency,
  CurrencyAmount,
  JSBI,
  Pair,
  Percent,
  Token,
  TokenAmount,
  Trade,
} from '../sdk'
import { computeTradePriceBreakdown } from './prices'

const TRADE_SEARCH_OPTS = { maxHops: 3, maxNumResults: 1 } as const
const MAX_BINARY_SEARCH_STEPS = 40

export function currencyAmountFromRaw(currency: Currency, raw: JSBI): CurrencyAmount {
  if (currency instanceof Token) return new TokenAmount(currency, raw)
  const nativeChainId = getNativeChainIdForCurrency(currency)
  if (nativeChainId !== undefined) return CurrencyAmount.native(nativeChainId, raw)
  return new TokenAmount(currency as Token, raw)
}

export function bestTradeExactIn(
  pairs: Pair[],
  amountIn: CurrencyAmount,
  currencyOut: Currency,
): Trade | null {
  if (!pairs.length || !amountIn.greaterThan(JSBI.BigInt(0))) return null
  return Trade.bestTradeExactIn(pairs, amountIn, currencyOut, TRADE_SEARCH_OPTS)[0] ?? null
}

export function isTradeWithinPriceImpactLimit(
  trade: Trade,
  chainId: ChainId | undefined,
  blockedThreshold?: Percent,
): boolean {
  const threshold = blockedThreshold ?? resolveMaxSwapPriceImpactPercent(chainId, trade)
  const { priceImpactWithoutFee } = computeTradePriceBreakdown(trade)
  if (!priceImpactWithoutFee) return true
  return priceImpactWithoutFee.lessThan(threshold)
}

export function resolveMaxInputWithinPriceImpact(
  pairs: Pair[],
  spendable: CurrencyAmount,
  currencyOut: Currency,
  chainId: ChainId | undefined,
): CurrencyAmount | undefined {
  if (!pairs.length || !spendable.greaterThan(JSBI.BigInt(0))) return undefined

  const fullTrade = bestTradeExactIn(pairs, spendable, currencyOut)
  if (fullTrade && isTradeWithinPriceImpactLimit(fullTrade, chainId)) {
    return spendable
  }

  let low = JSBI.BigInt(0)
  let high = spendable.raw
  let bestRaw = JSBI.BigInt(0)

  for (let step = 0; step < MAX_BINARY_SEARCH_STEPS; step++) {
    if (JSBI.greaterThan(low, high)) break

    const mid = JSBI.divide(JSBI.add(low, high), JSBI.BigInt(2))
    if (JSBI.equal(mid, JSBI.BigInt(0))) {
      if (JSBI.equal(low, high)) break
      low = JSBI.add(low, JSBI.BigInt(1))
      continue
    }

    const amount = currencyAmountFromRaw(spendable.currency, mid)
    const trade = bestTradeExactIn(pairs, amount, currencyOut)
    if (trade && isTradeWithinPriceImpactLimit(trade, chainId)) {
      bestRaw = mid
      low = JSBI.add(mid, JSBI.BigInt(1))
    } else {
      high = JSBI.subtract(mid, JSBI.BigInt(1))
    }
  }

  if (!JSBI.greaterThan(bestRaw, JSBI.BigInt(0))) return undefined
  return currencyAmountFromRaw(spendable.currency, bestRaw)
}

export function isInputAmountWithinPriceImpactLimit(
  pairs: Pair[],
  amountIn: CurrencyAmount,
  currencyOut: Currency,
  chainId: ChainId | undefined,
): boolean {
  const trade = bestTradeExactIn(pairs, amountIn, currencyOut)
  if (!trade) return false
  return isTradeWithinPriceImpactLimit(trade, chainId)
}
